"use client";

import { useState, useRef, useEffect } from "react";
import { Eye, Check } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import Helpers from "../Helper/Helpers";
import { useAuth } from "../context/AuthContextAdmin";

export default function SinglePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState("features");
  const { fetchProducts } = useAuth();

  const [inCart, setInCart] = useState(location?.state?.addtocartvalue); // ✅ new state to track add-to-cart
  const imageContainerRef = useRef(null);

  const images = location?.state?.images;
  const description = location?.state?.description;
  const features = location?.state?.features;
  const specifications = location?.state?.specifications;
  const details = location?.state?.details;

  const handleMouseMove = (e) => {
    const container = imageContainerRef.current;
    if (!container) return;

    const { left, top, width, height } = container.getBoundingClientRect();
    const x = (e.clientX - left) / width;
    const y = (e.clientY - top) / height;

    container.style.transformOrigin = `${x * 100}% ${y * 100}%`;
  };
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const HandleAddTocart = async (productId) => {
    if (productId) {
      const res = await Helpers(
        `/admin/product/${productId}/toggle-cart`,
        "PUT"
      );
      if (res && res?.status) {
        toast.success(res?.message);
        setInCart((prev) => !prev); // ✅ update local cart state
        fetchProducts();
      } else {
        toast.error("Failed to add data to cart");
      }
    }
  };

  return (
    <div
      className="min-h-screen bg-gray-50 py-12"
      style={{ marginTop: "77px" }}
    >
      <div className="container mx-auto px-4">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Product Images */}
          <div className="lg:w-1/2">
            <div className="relative bg-white rounded-xl shadow-md overflow-hidden">
              <div className="absolute top-4 left-4 z-10">
                <span className="bg-[#2F4858] text-white text-xs font-medium px-3 py-1 rounded">
                  SALE
                </span>
              </div>
              <div className="absolute top-4 right-4 z-10">
                <button className="bg-white rounded-full p-2 shadow-md hover:bg-gray-50 transition-colors">
                  <Eye className="w-4 h-4 text-gray-600" />
                </button>
              </div>
              <div
                ref={imageContainerRef}
                className="aspect-square overflow-hidden cursor-zoom-in"
                onMouseMove={handleMouseMove}
                onMouseEnter={() => {
                  if (imageContainerRef.current) {
                    imageContainerRef.current.style.transform = "scale(1.5)";
                  }
                }}
                onMouseLeave={() => {
                  if (imageContainerRef.current) {
                    imageContainerRef.current.style.transform = "scale(1)";
                  }
                }}
              >
                <img
                  src={images[currentImageIndex]}
                  alt="Product"
                  className="w-full h-full object-cover transition-transform duration-300"
                />
              </div>
            </div>

            <div className="grid grid-cols-4 gap-4 mt-4">
              {images.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`relative bg-white rounded-lg shadow-sm overflow-hidden aspect-square
                    ${
                      currentImageIndex === index ? "ring-2 ring-[#2F4858]" : ""
                    }`}
                >
                  <img
                    src={image}
                    alt={`Product view ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Product Details */}
          <div className="lg:w-1/2">
            <div className="bg-white rounded-xl shadow-md p-6">
              <h1 className="text-2xl font-bold mb-4">
                {location?.state?.name}
              </h1>

              <div className="flex items-baseline gap-2 mb-4">
                <span className="text-2xl font-bold">{`₹${location?.state?.price}`}</span>
                <span className="text-gray-500 line-through">{`₹${location?.state?.price}`}</span>
              </div>

              <div className="text-sm text-gray-600 mb-6">
                CATEGORY: {location?.state?.category?.name}
              </div>

              {/* Static Description */}
              <div className="mb-6">
                <h2 className="text-sm font-semibold text-gray-700 mb-1">
                  Description:
                </h2>
                <p className="text-sm text-gray-600">{description}</p>
              </div>

              <div className="flex items-center gap-4">
                <div className="flex items-center border rounded-lg shadow-sm">
                  <button
                    type="button"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="px-3 py-1 border-r hover:bg-gray-50"
                  >
                    -
                  </button>
                  <span className="px-4 py-1">{quantity}</span>
                  <button
                    type="button"
                    onClick={() => setQuantity(quantity + 1)}
                    className="px-3 py-1 border-l hover:bg-gray-50"
                  >
                    +
                  </button>
                </div>
                <button
                  type="submit"
                  onClick={() => HandleAddTocart(location?.state?._id)}
                  className="flex-1 bg-gradient-to-r from-[#2F4858] to-[#3f6175] text-white py-2 px-6 rounded-full shadow-md hover:from-[#1a2830] hover:to-[#2F4858] transition-all duration-300 ease-in-out"
                >
                  {inCart ? "Remove from Cart" : "Add to Cart"}
                </button>
              </div>

              {/* Tabs Section */}
              <div className="mt-8">
                <div className="flex border-b mb-4">
                  {["features", "specifications", "details"].map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`pb-2 flex-1 capitalize text-sm font-semibold text-center ${
                        activeTab === tab
                          ? "border-b-2 border-[#2F4858] text-[#2F4858]"
                          : "text-gray-500 hover:text-[#2F4858]"
                      }`}
                    >
                      {tab}
                    </button>
                  ))}
                </div>

                <ul className="space-y-2 text-sm text-gray-600">
                  {(activeTab === "features"
                    ? features
                    : activeTab === "specifications"
                    ? specifications
                    : details
                  ).map((item, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-500 mt-1" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
